// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface TaskNotification {
  id: string;
  task_id: string;
  athlete_profile_id: string;
  scout_user_id: string;
  status: 'pending' | 'read' | 'dismissed';
  created_at: string;
  task: {
    id: string;
    subject: string;
    description: string;
    activity_date: string;
    activity_type: string;
    recruiting_tracker_id?: string;
  };
  scout: {
    name: string;
    email: string;
  };
}

export const useAthleteTaskNotifications = () => {
  const { profile } = useAuth();
  const [notifications, setNotifications] = useState<TaskNotification[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchNotifications = async () => {
    if (!profile?.id || !profile?.user_id) {
      setIsLoading(false);
      return;
    }

    try {
      // Fetch task assignment notifications from the general notifications table
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', profile.user_id)
        .eq('type', 'task_assignment')
        .eq('read', false)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching task notifications:', error);
      } else {
        // Transform notifications to match expected TaskNotification interface
        const transformedData = (data || []).map(notification => ({
          id: notification.id,
          task_id: notification.related_id || '',
          athlete_profile_id: profile.id,
          scout_user_id: notification.user_id || '',
          status: (notification.read ? 'read' : 'pending') as 'pending' | 'read' | 'dismissed',
          created_at: notification.created_at,
          task: {
            id: notification.related_id || '',
            subject: notification.title || '',
            description: notification.message || '',
            activity_date: '',
            activity_type: 'task_assignment',
            recruiting_tracker_id: ''
          },
          scout: {
            name: 'Scout',
            email: ''
          }
        }));
        setNotifications(transformedData);
      }
    } catch (err) {
      console.error('Error in fetchNotifications:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const acceptTask = async (notificationId: string, taskId: string) => {
    if (!profile?.id) return false;

    try {
      console.log('🔄 Accepting task:', { notificationId, taskId, profileId: profile.id });

      // First, get the notification details to extract task information
      const { data: notification, error: notificationFetchError } = await supabase
        .from('notifications')
        .select('*')
        .eq('id', notificationId)
        .single();

      if (notificationFetchError || !notification) {
        console.error('Error fetching notification:', notificationFetchError);
        toast.error('Failed to fetch task details');
        return false;
      }

      // Create the task in recruiting_tasks table
      const { error: taskCreateError } = await supabase
        .from('recruiting_tasks')
        .insert({
          profile_id: profile.id,
          title: notification.title || 'Scout Assigned Task',
          notes: notification.message || '',
          status: 'new',
          source_assignment_id: notification.related_id, // Link back to original assignment
        });

      if (taskCreateError) {
        console.error('Error creating task in recruiting_tasks:', taskCreateError);
        toast.error('Failed to create task');
        return false;
      }

      // Mark notification as read in the notifications table
      const { error: notificationError } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId);

      if (notificationError) {
        console.error('Error updating notification:', notificationError);
        toast.error('Failed to update notification');
        return false;
      }

      console.log('✅ Task accepted and created in recruiting_tasks table');
      toast.success('Task accepted and added to your tracker');
      await fetchNotifications();
      return true;
    } catch (err) {
      console.error('Error in acceptTask:', err);
      toast.error('Failed to accept task');
      return false;
    }
  };

  const declineTask = async (notificationId: string, taskId: string) => {
    if (!profile?.id) return false;

    try {
      // Mark notification as read/dismissed in the notifications table
      const { error: notificationError } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId);

      if (notificationError) {
        console.error('Error updating notification:', notificationError);
        toast.error('Failed to update notification');
        return false;
      }

      toast.success('Task declined');
      await fetchNotifications();
      return true;
    } catch (err) {
      console.error('Error in declineTask:', err);
      toast.error('Failed to decline task');
      return false;
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [profile?.id, profile?.user_id]);

  return {
    notifications,
    isLoading,
    acceptTask,
    declineTask,
    refreshNotifications: fetchNotifications
  };
};